"use client"

import { useEffect, useRef, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Card } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { useLocale } from "@/context/LocaleContext"
import { tabSwitchVariants, slideVariants, slideUpVariants } from "@/utils/animation"
import FeaturePanelContent from "./FeaturePanelContent";

interface ListGroup {
    titleKey: string
    titleFallback: string
    items: { key: string; fallback: string }[]
}

export interface FeatureTab {
    id: string
    titleKey: string
    titleFallback: string
    descKey: string
    descFallback: string
    descBoldKey?: string
    descBoldFallback?: string
    desc2Key?: string
    desc2Fallback?: string
    listGroups: ListGroup[]
    btnKey: string
    btnFallback: string
    href: string
    image: string
    altKey: string
    altFallback: string
}

const FEATURES: FeatureTab[] = [
    {
        id: "custom-rsvp",
        titleKey: "LandingPage.custom_rsvp_title",
        titleFallback: "Fleksibel Custom RSVP",
        descKey: "LandingPage.custom_rsvp_desc_1",
        descFallback:
            "Kelola konfirmasi kehadiran tamu dengan sistem RSVP yang dapat disesuaikan sepenuhnya. Kamu bisa mengatur pertanyaan RSVP sesuai kebutuhan acara, menentukan status kehadiran, serta ",
        descBoldKey: "LandingPage.custom_rsvp_desc_bold",
        descBoldFallback: "membatasi jumlah orang yang boleh dibawa oleh setiap tamu.",
        listGroups: [
            {
                titleKey: "LandingPage.custom_rsvp_list_title",
                titleFallback: "Fitur ini membantu kamu:",
                items: [
                    { key: "LandingPage.custom_rsvp_list_1", fallback: "Menjaga kapasitas acara tetap sesuai rencana" },
                    { key: "LandingPage.custom_rsvp_list_2", fallback: "Menghindari kehadiran berlebih" },
                    { key: "LandingPage.custom_rsvp_list_3", fallback: "Mengelola tamu dengan lebih rapi dan terkontrol" },
                ],
            },
        ],
        btnKey: "LandingPage.custom_rsvp_btn",
        btnFallback: "Lihat Selengkapnya",
        href: "/features/custom-rsvp",
        image: "/images/cta/custom-rsvp.png",
        altKey: "LandingPage.custom_rsvp_img_alt",
        altFallback: "Fitur Custom RSVP Elynk",
    },
    {
        id: "sebar-otomatis",
        titleKey: "LandingPage.sebar_otomatis_title",
        titleFallback: "Sebar Otomatis via WhatsApp",
        descKey: "LandingPage.sebar_otomatis_desc",
        descFallback:
            "Sebarkan undangan digital ke seluruh tamu secara otomatis melalui WhatsApp. Tidak perlu mengirim satu per satu secara manual cukup siapkan daftar tamu, dan sistem akan membantu proses pengiriman secara terstruktur.",
        listGroups: [
            {
                titleKey: "LandingPage.sebar_otomatis_list_title",
                titleFallback: "Hasilnya:",
                items: [
                    { key: "LandingPage.sebar_otomatis_list_1", fallback: "Proses sebar undangan lebih cepat" },
                    { key: "LandingPage.sebar_otomatis_list_2", fallback: "Minim kesalahan pengiriman" },
                    { key: "LandingPage.sebar_otomatis_list_3", fallback: "Lebih efisien untuk tamu dalam jumlah besar" },
                ],
            },
        ],
        btnKey: "LandingPage.sebar_otomatis_btn",
        btnFallback: "Lihat Selengkapnya",
        href: "/features/whatsapp-broadcast",
        image: "/images/cta/sebar-otomatis.png",
        altKey: "LandingPage.sebar_otomatis_img_alt",
        altFallback: "Sebar Otomatis via WhatsApp Elynk",
    },
    {
        id: "qr-checkin",
        titleKey: "LandingPage.qr_checkin_title",
        titleFallback: "QR Code Check-in Buku Tamu",
        descKey: "LandingPage.qr_checkin_desc",
        descFallback:
            "Setiap tamu mendapatkan QR Code unik untuk proses check-in di lokasi acara. Saat hari acara, tamu cukup memindai QR Code untuk melakukan registrasi kehadiran.",
        listGroups: [
            {
                titleKey: "LandingPage.qr_checkin_list_title",
                titleFallback: "Keuntungannya:",
                items: [
                    { key: "LandingPage.qr_checkin_list_1", fallback: "Proses check-in lebih cepat dan tertib" },
                    { key: "LandingPage.qr_checkin_list_2", fallback: "Data kehadiran tercatat otomatis" },
                    { key: "LandingPage.qr_checkin_list_3", fallback: "Cocok untuk acara formal dan profesional" },
                ],
            },
        ],
        btnKey: "LandingPage.qr_checkin_btn",
        btnFallback: "Lihat Selengkapnya",
        href: "/features/qr-check-in",
        image: "/images/cta/qr-code.png",
        altKey: "LandingPage.qr_checkin_img_alt",
        altFallback: "QR Code Check-in Buku Tamu Elynk",
    },
    {
        id: "guest-split",
        titleKey: "LandingPage.guest_split_title",
        titleFallback: "Split Acara Setiap Tamu",
        descKey: "LandingPage.guest_split_desc",
        descFallback:
            "Dalam satu undangan, kamu bisa membuat beberapa acara berbeda (misal akad, resepsi, after party, atau sesi khusus lainnya).",
        listGroups: [
            {
                titleKey: "LandingPage.guest_split_list1_title",
                titleFallback: "Setiap tamu dapat diatur:",
                items: [
                    { key: "LandingPage.guest_split_list1_1", fallback: "Hanya diundang ke acara tertentu" },
                    { key: "LandingPage.guest_split_list1_2", fallback: "Tidak bisa mengakses acara yang bukan untuknya" },
                ],
            },
            {
                titleKey: "LandingPage.guest_split_list2_title",
                titleFallback: "Fitur ini sangat ideal untuk:",
                items: [
                    { key: "LandingPage.guest_split_list2_1", fallback: "Acara dengan segmentasi tamu" },
                    { key: "LandingPage.guest_split_list2_2", fallback: "Intimate wedding" },
                    { key: "LandingPage.guest_split_list2_3", fallback: "Event yang membutuhkan kontrol akses" },
                ],
            },
        ],
        btnKey: "LandingPage.guest_split_btn",
        btnFallback: "Lihat Selengkapnya",
        href: "/features/multi-event-access",
        image: "/images/cta/split-tamu.png",
        altKey: "LandingPage.guest_split_img_alt",
        altFallback: "Split Acara Setiap Tamu Elynk",
    },
    {
        id: "digital-envelope",
        titleKey: "LandingPage.digital_envelope_title",
        titleFallback: "Amplop Digital Otomatis",
        descKey: "LandingPage.digital_envelope_desc_1",
        descFallback:
            "Elynk menyediakan sistem amplop digital terintegrasi yang memungkinkan tamu mengirimkan hadiah berupa uang secara online.",
        desc2Key: "LandingPage.digital_envelope_desc_2",
        desc2Fallback: "Semua proses berjalan aman dan transparan.",
        listGroups: [
            {
                titleKey: "LandingPage.digital_envelope_list_title",
                titleFallback: "Pemilik acara akan mendapatkan:",
                items: [
                    { key: "LandingPage.digital_envelope_list_1", fallback: "Notifikasi realtime setiap ada amplop masuk" },
                    { key: "LandingPage.digital_envelope_list_2", fallback: "Riwayat transaksi yang jelas" },
                    { key: "LandingPage.digital_envelope_list_3", fallback: "Kemudahan penarikan dana (withdraw)" },
                ],
            },
        ],
        btnKey: "LandingPage.digital_envelope_btn",
        btnFallback: "Lihat Selengkapnya",
        href: "/features/digital-envelope",
        image: "/images/cta/amplop-digital.png",
        altKey: "LandingPage.digital_envelope_img_alt",
        altFallback: "Amplop Digital Otomatis Elynk",
    },
    {
        id: "physical-gift",
        titleKey: "LandingPage.physical_gift_title",
        titleFallback: "Titip Kado Otomatis",
        descKey: "LandingPage.physical_gift_desc_1",
        descFallback:
            "Tamu dapat memilih dan membeli kado untuk pemilik acara langsung melalui Elynk. Kami yang akan menyiapkan dan mengirimkan kado tersebut ke alamat tujuan.",
        desc2Key: "LandingPage.physical_gift_desc_2",
        desc2Fallback: "Tanpa perlu repot koordinasi pengiriman kado secara manual.",
        listGroups: [
            {
                titleKey: "LandingPage.physical_gift_list_title",
                titleFallback: "Baik tamu maupun pemilik acara akan mendapatkan:",
                items: [
                    { key: "LandingPage.physical_gift_list_1", fallback: "Informasi pengiriman" },
                    { key: "LandingPage.physical_gift_list_2", fallback: "Link tracking secara realtime" },
                ],
            },
        ],
        btnKey: "LandingPage.physical_gift_btn",
        btnFallback: "Lihat Selengkapnya",
        href: "/features/gift-registry",
        image: "/images/cta/kado-fisik.png",
        altKey: "LandingPage.physical_gift_img_alt",
        altFallback: "Titip Kado Otomatis Elynk",
    },
]

export default function FeatureTabs() {
    const { t } = useLocale()
    const [active, setActive] = useState(0)
    const current = FEATURES[active]
    const total = FEATURES.length

    // ===== Slider mobile =====
    const mobileScrollRef = useRef<HTMLDivElement | null>(null)
    const chipRefs = useRef<(HTMLButtonElement | null)[]>([])
    const scrollTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
    const isFirstChipScroll = useRef(true)

    useEffect(() => {
        // Skip run pertama (mount) supaya tidak trigger auto-scroll halaman
        // ke section ini saat reload/pertama render di mobile.
        if (isFirstChipScroll.current) {
            isFirstChipScroll.current = false
            return
        }
        chipRefs.current[active]?.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
        })
    }, [active])

    useEffect(() => {
        return () => {
            if (scrollTimeout.current) clearTimeout(scrollTimeout.current)
        }
    }, [])

    const handleMobileScroll = () => {
        const el = mobileScrollRef.current
        if (!el) return
        if (scrollTimeout.current) clearTimeout(scrollTimeout.current)
        scrollTimeout.current = setTimeout(() => {
            if (!el || el.clientWidth === 0) return
            const index = Math.round(el.scrollLeft / el.clientWidth)
            if (index !== active && index >= 0 && index < total) {
                setActive(index)
            }
        }, 100)
    }

    const goToSlide = (index: number) => {
        setActive(index)
        const el = mobileScrollRef.current
        if (el) {
            el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" })
        }
    }

    return (
        <section className="bg-white px-4 py-14 sm:px-6 md:py-20">
            <div className="mx-auto max-w-[1160px]">

                {/* ===== Desktop: sidebar + panel ===== */}
                <div className="hidden items-start gap-12 lg:grid lg:grid-cols-[340px_1fr]">
                    <motion.div
                        variants={slideVariants(true, 0.6)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                        className="flex flex-col gap-2.5"
                    >
                        {FEATURES.map((feature, index) => {
                            const isActive = index === active
                            return (
                                <button
                                    key={feature.id}
                                    onClick={() => setActive(index)}
                                    aria-pressed={isActive}
                                    className={cn(
                                        "flex items-center gap-3.5 rounded-[14px] border px-[18px] py-4 text-left transition-colors duration-200",
                                        isActive
                                            ? "border-[#D9DCE1] bg-[#F4F5F7]"
                                            : "border-[#EEF0F3] bg-white hover:border-[#D9DCE1]"
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] font-[Poppins,sans-serif] text-[15px] font-semibold transition-colors duration-200",
                                            isActive ? "bg-[#ED6A57] text-white" : "bg-[#F1F2F5] text-[#9AA0AA]"
                                        )}
                                    >
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <span className="font-[Poppins,sans-serif] text-[16px] font-semibold leading-tight text-[#2E2E38]">
                                        {t(feature.titleKey) || feature.titleFallback}
                                    </span>
                                </button>
                            )
                        })}
                    </motion.div>

                    <motion.div
                        variants={slideVariants(false, 0.6)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <motion.div layout transition={{ duration: 0.25, ease: "easeInOut" }}>
                            <Card className="overflow-hidden rounded-[24px] border-0 p-11 shadow-[0_14px_40px_rgba(30,30,50,0.06)]">
                                <AnimatePresence mode="wait">
                                    <motion.div
                                        key={current.id}
                                        variants={tabSwitchVariants(0.12)}
                                        initial="hidden"
                                        animate="visible"
                                        exit="exit"
                                    >
                                        <FeaturePanelContent feature={current} t={t} />
                                    </motion.div>
                                </AnimatePresence>
                            </Card>
                        </motion.div>
                    </motion.div>
                </div>

                {/* ===== Mobile: swipeable slider ===== */}
                <motion.div
                    variants={slideUpVariants(0.6)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.2 }}
                    className="lg:hidden"
                >
                    <div className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 pb-1 no-scrollbar sm:-mx-6 sm:px-6">
                        {FEATURES.map((feature, index) => {
                            const isActive = index === active
                            return (
                                <button
                                    key={feature.id}
                                    ref={(el) => {
                                        chipRefs.current[index] = el
                                    }}
                                    onClick={() => goToSlide(index)}
                                    aria-current={isActive}
                                    className={cn(
                                        "flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-left transition-colors duration-200",
                                        isActive
                                            ? "border-[#D9DCE1] bg-[#F4F5F7]"
                                            : "border-[#EEF0F3] bg-white"
                                    )}
                                >
                                    <span
                                        className={cn(
                                            "flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full font-[Poppins,sans-serif] text-[11px] font-semibold transition-colors duration-200",
                                            isActive ? "bg-[#ED6A57] text-white" : "bg-[#F1F2F5] text-[#9AA0AA]"
                                        )}
                                    >
                                        {String(index + 1).padStart(2, "0")}
                                    </span>
                                    <span className="whitespace-nowrap font-[Poppins,sans-serif] text-[13px] font-semibold text-[#2E2E38]">
                                        {t(feature.titleKey) || feature.titleFallback}
                                    </span>
                                </button>
                            )
                        })}
                    </div>

                    <div
                        ref={mobileScrollRef}
                        onScroll={handleMobileScroll}
                        className="-mx-4 flex snap-x snap-mandatory overflow-x-auto px-4 no-scrollbar sm:-mx-6 sm:px-6"
                    >
                        {FEATURES.map((feature) => (
                            <div key={feature.id} className="w-full shrink-0 snap-center">
                                <Card className="overflow-hidden rounded-[20px] border-0 p-5 shadow-[0_10px_30px_rgba(30,30,50,0.06)] sm:p-6">
                                    <FeaturePanelContent feature={feature} t={t} />
                                </Card>
                            </div>
                        ))}
                    </div>
                </motion.div>

            </div>
        </section>
    )
}